import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';

const STATUS_LABELS = {
  pending: 'Pending',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'today', label: 'Today' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const toDate = (timestamp) => {
  if (!timestamp) return null;
  try {
    return timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  } catch {
    return null;
  }
};

const formatCreatedAt = (timestamp) => {
  const date = toDate(timestamp);
  if (!date) return 'Just now';
  return date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const formatPreferredDate = (value) => {
  if (!value) return 'No date';
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const formatPreferredTime = (value) => {
  if (!value) return 'No time';
  const [hourString, minuteString = '00'] = value.split(':');
  const hourValue = Number(hourString);
  if (Number.isNaN(hourValue)) return value;
  const period = hourValue >= 12 ? 'PM' : 'AM';
  const hour = hourValue % 12 || 12;
  return `${hour}:${minuteString.padStart(2, '0')} ${period}`;
};

const getTodayValue = (today = new Date()) => {
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const date = `${today.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${date}`;
};

const getScheduledDateTime = (item) => {
  if (!item.preferredDate || !item.preferredTime) return null;
  const date = new Date(`${item.preferredDate}T${item.preferredTime}:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDuration = (milliseconds) => {
  const totalMinutes = Math.max(0, Math.floor(Math.abs(milliseconds) / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const getScheduleTiming = (item, now = new Date()) => {
  if (item.status === 'completed') {
    return { label: 'Completed', tone: 'done', milliseconds: null };
  }

  if (item.status === 'cancelled') {
    return { label: 'Cancelled', tone: 'muted', milliseconds: null };
  }

  const scheduledAt = getScheduledDateTime(item);
  if (!scheduledAt) return { label: 'Time not set', tone: 'muted', milliseconds: null };

  const milliseconds = scheduledAt.getTime() - now.getTime();
  if (milliseconds < 0) {
    return {
      label: `Overdue by ${formatDuration(milliseconds)}`,
      tone: 'overdue',
      milliseconds,
    };
  }

  if (milliseconds <= 15 * 60000) {
    return {
      label: `Due in ${formatDuration(milliseconds)}`,
      tone: 'urgent',
      milliseconds,
    };
  }

  return {
    label: `In ${formatDuration(milliseconds)}`,
    tone: 'upcoming',
    milliseconds,
  };
};

const getScheduleBucket = (item, now = new Date()) => {
  if (item.status === 'completed') return 'completed';
  if (item.status === 'cancelled') return 'cancelled';

  const scheduledAt = getScheduledDateTime(item);
  if (scheduledAt && scheduledAt.getTime() < now.getTime()) return 'overdue';

  const todayValue = getTodayValue(now);
  if (item.preferredDate === todayValue) return 'today';
  if (item.preferredDate && item.preferredDate < todayValue) return 'overdue';
  return 'upcoming';
};

const normalizePhone = (phone) => String(phone || '').replace(/\D/g, '');

function StatCard({ label, value, tone }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2d7c6', borderRadius: 4, padding: '11px 12px', minWidth: 0 }}>
      <div style={{ fontSize: '1.18rem', fontWeight: 900, color: tone, lineHeight: 1 }}>{value}</div>
      <div style={{ marginTop: 5, fontSize: '0.64rem', color: '#6A7079', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    </div>
  );
}

function CallScheduleCard({ item, now, onStatusChange, onDelete }) {
  const phoneDigits = normalizePhone(item.phone);
  const phoneForWhatsApp = phoneDigits.length === 10 ? `91${phoneDigits}` : phoneDigits;
  const bucket = getScheduleBucket(item, now);
  const status = item.status || 'pending';
  const statusColor = status === 'completed' ? '#187044' : status === 'cancelled' ? '#9f2f26' : bucket === 'overdue' ? '#b45309' : '#1d4f91';
  const timing = getScheduleTiming(item, now);
  const timerStyle = timing.tone === 'overdue'
    ? overdueTimerStyle
    : timing.tone === 'urgent'
      ? urgentTimerStyle
      : timing.tone === 'done'
        ? doneTimerStyle
        : timing.tone === 'muted'
          ? mutedTimerStyle
          : upcomingTimerStyle;

  return (
    <article style={{ background: '#fff', border: `1px solid ${bucket === 'overdue' ? '#e7b985' : '#D7DBDF'}`, borderRadius: 4, padding: 16, boxShadow: '0 1px 2px rgba(31,22,10,0.04)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr) auto', gap: 14, alignItems: 'start' }}>
        <div style={{ width: 42, height: 42, borderRadius: 4, background: '#17110a', color: '#d8b36a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>
          C
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 7 }}>
            <span style={{ fontSize: '0.72rem', color: '#8b6f42', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {item.sourceLabel || 'Measurement Help'}
            </span>
            <span style={{ padding: '3px 8px', borderRadius: 3, background: '#F7F8F9', color: statusColor, fontSize: '0.68rem', fontWeight: 900 }}>
              {STATUS_LABELS[status] || STATUS_LABELS.pending}
            </span>
            <span style={{ fontSize: '0.74rem', color: '#6A7079', fontWeight: 650 }}>{formatCreatedAt(item.createdAt)}</span>
          </div>

          <h3 style={{ margin: '0 0 7px', color: '#17110a', fontSize: '1rem', fontWeight: 900, lineHeight: 1.25 }}>
            {item.name || 'Customer'}
          </h3>

          <div style={timerStyle}>
            <span style={{ fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Call Timer
            </span>
            <strong style={{ fontSize: '0.92rem', lineHeight: 1.25 }}>{timing.label}</strong>
          </div>

          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={chipStyle}><b>Phone:</b> {item.phone || 'Not provided'}</span>
            <span style={chipStyle}><b>Date:</b> {item.preferredDateLabel || formatPreferredDate(item.preferredDate)}</span>
            <span style={chipStyle}><b>Time:</b> {item.preferredTimeLabel || formatPreferredTime(item.preferredTime)}</span>
            {item.sourcePage && <span style={chipStyle}><b>Page:</b> {item.sourcePage}</span>}
          </div>

          {item.note && (
            <p style={{ margin: 0, color: '#5e4d39', fontSize: '0.84rem', lineHeight: 1.55, background: '#F7F8F9', border: '1px solid #D7DBDF', borderRadius: 4, padding: '9px 11px' }}>
              {item.note}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', minWidth: 300 }}>
          {phoneDigits && (
            <>
              <a href={`tel:+${phoneForWhatsApp}`} style={actionButtonStyle}>Call</a>
              <a href={`https://wa.me/${phoneForWhatsApp}`} target="_blank" rel="noreferrer" style={secondaryButtonStyle}>WhatsApp</a>
            </>
          )}
          <button type="button" onClick={() => onStatusChange(item.id, 'completed')} style={secondaryButtonStyle}>Done</button>
          <button type="button" onClick={() => onStatusChange(item.id, 'cancelled')} style={lightButtonStyle}>Cancel</button>
          <button type="button" onClick={() => onDelete(item.id)} style={dangerButtonStyle}>Delete</button>
        </div>
      </div>
    </article>
  );
}

const chipStyle = {
  fontSize: '0.73rem',
  color: '#67543b',
  background: '#F7F8F9',
  border: '1px solid #D7DBDF',
  borderRadius: 3,
  padding: '4px 9px',
  maxWidth: 260,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const actionButtonStyle = {
  background: '#15100a',
  color: '#fff',
  border: '1px solid #15100a',
  borderRadius: 4,
  padding: '8px 12px',
  fontSize: '0.74rem',
  fontWeight: 900,
  cursor: 'pointer',
  textDecoration: 'none',
  height: 35,
  display: 'inline-flex',
  alignItems: 'center',
};

const secondaryButtonStyle = {
  background: '#faf7f1',
  border: '1px solid #D7DBDF',
  borderRadius: 4,
  padding: '8px 11px',
  cursor: 'pointer',
  color: '#80643d',
  fontSize: '0.74rem',
  fontWeight: 900,
  textDecoration: 'none',
  height: 35,
  display: 'inline-flex',
  alignItems: 'center',
};

const lightButtonStyle = {
  ...secondaryButtonStyle,
  color: '#7a6750',
};

const dangerButtonStyle = {
  ...secondaryButtonStyle,
  background: '#fff',
  border: '1px solid #f0cfca',
  color: '#b33b2f',
};

const baseTimerStyle = {
  width: 'fit-content',
  maxWidth: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 9,
  marginBottom: 10,
  borderRadius: 3,
  padding: '7px 11px',
  border: '1px solid #D7DBDF',
};

const upcomingTimerStyle = {
  ...baseTimerStyle,
  background: '#eef7f1',
  borderColor: '#cdebd8',
  color: '#187044',
};

const urgentTimerStyle = {
  ...baseTimerStyle,
  background: '#fff4e5',
  borderColor: '#efd1a4',
  color: '#9a5b00',
};

const overdueTimerStyle = {
  ...baseTimerStyle,
  background: '#fff1ec',
  borderColor: '#f0c1b4',
  color: '#b33b2f',
};

const doneTimerStyle = {
  ...baseTimerStyle,
  background: '#eefaf3',
  borderColor: '#cdebd8',
  color: '#187044',
};

const mutedTimerStyle = {
  ...baseTimerStyle,
  background: '#F7F8F9',
  borderColor: '#D7DBDF',
  color: '#7a6750',
};

function CallSchedulePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const unsubscribe = db.collection('CallSchedules')
      .orderBy('createdAt', 'desc')
      .limit(200)
      .onSnapshot(
        (snapshot) => {
          setItems(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching call schedules:', error);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, []);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      if (filter !== 'all' && getScheduleBucket(item, now) !== filter) return false;
      if (!query) return true;
      return [item.name, item.phone, item.note, item.sourcePage, item.sourceLabel]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [items, filter, search, now]);

  const counts = useMemo(() => {
    return items.reduce((summary, item) => {
      const bucket = getScheduleBucket(item, now);
      summary.all += 1;
      summary[bucket] = (summary[bucket] || 0) + 1;
      return summary;
    }, { all: 0, today: 0, upcoming: 0, overdue: 0, completed: 0, cancelled: 0 });
  }, [items, now]);

  const handleStatusChange = async (id, status) => {
    try {
      await db.collection('CallSchedules').doc(id).update({
        status,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error updating call schedule:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await db.collection('CallSchedules').doc(id).delete();
    } catch (error) {
      console.error('Error deleting call schedule:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6A7079', fontWeight: 800 }}>
        Loading call schedules...
      </div>
    );
  }

  return (
    <div style={{ padding: '22px 26px 34px', minHeight: '100vh', background: '#EEF0F2', boxSizing: 'border-box' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(244,241,235,0.96)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #ded4c4', paddingBottom: 14, marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: '0.72rem', color: '#6A7079', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 5 }}>
              MAVIINCI Follow-up
            </div>
            <h2 style={{ margin: 0, color: '#17110a', fontSize: '1.65rem', fontWeight: 900, lineHeight: 1.05 }}>
              Call Schedule
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(82px, 1fr))', gap: 8, flex: '0 1 560px' }}>
            <StatCard label="Total" value={counts.all} tone="#17110a" />
            <StatCard label="Today" value={counts.today} tone="#1d4f91" />
            <StatCard label="Upcoming" value={counts.upcoming} tone="#187044" />
            <StatCard label="Overdue" value={counts.overdue} tone="#b45309" />
            <StatCard label="Done" value={counts.completed} tone="#187044" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) auto', gap: 10, alignItems: 'center', marginBottom: 10 }}>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, phone, note, source page..."
            style={{ width: '100%', padding: '11px 13px', borderRadius: 4, border: '1px solid #d8cab5', background: '#fff', fontSize: '0.86rem', color: '#2c1e0a', outline: 'none', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 6, background: '#fff', border: '1px solid #d8cab5', borderRadius: 4, padding: 5, overflowX: 'auto' }}>
            {FILTERS.map((item) => {
              const active = filter === item.key;
              return (
                <button key={item.key} type="button" onClick={() => setFilter(item.key)} style={{ background: active ? '#17110a' : 'transparent', color: active ? '#fff' : '#6f604e', border: 'none', borderRadius: 9, padding: '8px 11px', fontSize: '0.74rem', fontWeight: 850, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                  {item.label}
                  <span style={{ background: active ? 'rgba(255,255,255,0.2)' : '#e8ddd0', color: active ? '#fff' : '#6A7079', borderRadius: 3, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 800, minWidth: 20, textAlign: 'center' }}>{counts[item.key] || 0}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.86rem', color: '#6f604e', fontWeight: 800 }}>
          {filteredItems.length} visible
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 20px', background: '#fff', borderRadius: 4, border: '1px solid #D7DBDF' }}>
          <h4 style={{ margin: '0 0 8px', fontWeight: 800, color: '#14110d', fontSize: '1.1rem' }}>No call schedules found</h4>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#8c7558' }}>
            Measurement help callback requests will appear here in real time.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredItems.map((item) => (
            <CallScheduleCard
              key={item.id}
              item={item}
              now={now}
              onStatusChange={handleStatusChange}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default CallSchedulePage;